<template>
	<component :is="componentTag" :class="componentClasses" v-bind="componentProps" v-on="componentListeners">
		<slot/>
	</component>
</template>

<!-- Card component that is displayed as relative positioned card on desktop devices and as floating panel on smaller screen sizes -->
<script setup lang="ts">
	import {useNuxtApp} from "#app";
	import {computed, ref, watch} from "vue";
	import 'vant/es/floating-panel/style';

	const props = withDefaults(defineProps<{

		// The class to apply to the root element when rendered as relative card
		cardClass?: string,

		cardTransparent?: boolean,

		// The synchronized height of the root element when rendered as floating panel
		floatingHeight?: number,

		// The minimum height of the root element when rendered as floating panel
		floatingMinHeight?: number,

		// The anchors to use for the floating panel. Might be relative to the screen height when suffixed with percentage or absolute in pixels otherwise.
		floatingAnchors?: (string|number)[],
	}>(), {
		floatingMinHeight: 200, // Default height to display one and a half profile previews above app mobile nav
		floatingAnchors: ['60%']
	});
	const emit = defineEmits(['update:floatingHeight']);

	const {$device} = useNuxtApp();

	const currentFloatingHeight = ref(props.floatingHeight ?? props.floatingMinHeight);
	watch(() => props.floatingHeight, (newValue, oldValue) => {
		if (newValue !== oldValue) {
			currentFloatingHeight.value = newValue;
		}
	});
	watch(currentFloatingHeight, (newValue, oldValue) => {
		if (newValue !== oldValue) {
			emit('update:floatingHeight', newValue);
		}
	});

	const showFloatingStyle = computed(() => {
		return !$device.isDesktop;
	});

	const componentTag = computed(() => {
		return showFloatingStyle.value ?  resolveComponent('VanFloatingPanel') : resolveComponent('Card');
	});

	const componentClasses = computed(() => {
		if (showFloatingStyle.value) {
			return ['pb-48']; // Respect mobile nav height
		}

		return props.cardClass ?? [];
	});

	const componentProps = computed(() => {
		if (showFloatingStyle.value) {
			return {
				height: currentFloatingHeight.value,
				anchors: floatingAnchors.value,
				contentDraggable: false // Weak UX on event index page
			}
		}

		return {
			flat: props.cardTransparent,
			color: props.cardTransparent ? 'transparent' : undefined
		}
	});

	const componentListeners = computed(() => {
		if (showFloatingStyle.value) {
			return {
				heightChange: ({height}) => {
					currentFloatingHeight.value = height
				},
			}
		}

		return {}
	});

	const floatingAnchors = computed(() => {
		const isWindowDefined = typeof window !== 'undefined';

		return [
			props.floatingMinHeight,
			...props.floatingAnchors.map(anchor => {
				if (typeof anchor === 'string' && anchor.endsWith('%')) {
					return isWindowDefined ? Math.round(parseInt(anchor.split('%')[0]) / 100 * window.innerHeight) : currentFloatingHeight.value;
				}

				return anchor;
			}),
		]
	});
</script>

<style scoped>

</style>